import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupsIcon from '@mui/icons-material/Groups';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import React, { useMemo, useState } from 'react';
import { TeamGroupTeamDto } from 'src/api/teamGroups';
import { ContactListMember } from '../types';
import {
  applyPreviousTeams,
  autoDistributeWithOptions,
  AutoDistributeMode,
  AutoDistributeStrategy,
  EditableTeam,
  getAssignableContacts,
  perTeamRangeLabel,
} from '../utils/teamGroups';
import { ManualTeamBoard } from './ManualTeamBoard';
import styles from './TeamGroupEditor.module.css';

type TeamGroupEditorProps = {
  groupName: string;
  onGroupNameChange: (name: string) => void;
  contacts: ContactListMember[];
  teams: EditableTeam[];
  previousTeams: TeamGroupTeamDto[];
  currentUserId?: string;
  onChange: (teams: EditableTeam[]) => void;
  onSelectMember?: (member: ContactListMember, teamName?: string) => void;
};

type EditorMode = 'auto' | 'manual';

const participantWord = (count: number) =>
  count === 1 ? 'deltaker' : 'deltakere';

const Toggle: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label: string;
  description: string;
  hint: string;
}> = ({ checked, onChange, disabled, label, description, hint }) => (
  <label className={styles.toggleRow}>
    <span className={styles.switch}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={styles.switchTrack} />
    </span>
    <span className={styles.toggleCopy}>
      <span className={styles.toggleLabel}>
        {label}
        <span className={styles.infoHint} title={hint}>
          <InfoOutlinedIcon />
        </span>
      </span>
      <span className={styles.toggleDescription}>{description}</span>
    </span>
  </label>
);

export const TeamGroupEditor: React.FC<TeamGroupEditorProps> = ({
  groupName,
  onGroupNameChange,
  contacts,
  teams,
  previousTeams,
  currentUserId,
  onChange,
  onSelectMember,
}) => {
  const [editorMode, setEditorMode] = useState<EditorMode>('auto');
  const [distributeMode, setDistributeMode] =
    useState<AutoDistributeMode>('teamCount');
  const [teamCount, setTeamCount] = useState(4);
  const [membersPerTeam, setMembersPerTeam] = useState(4);
  const [keepTogether, setKeepTogether] = useState(true);
  const [balanceByCompany, setBalanceByCompany] = useState(false);
  const [usePrevious, setUsePrevious] = useState(false);
  const [showDetailedPreview, setShowDetailedPreview] = useState(false);

  const assignableContacts = useMemo(
    () => getAssignableContacts(contacts),
    [contacts]
  );
  const poolIds = useMemo(
    () => new Set(assignableContacts.map((contact) => contact.id)),
    [assignableContacts]
  );
  const contactsById = useMemo(
    () => new Map(contacts.map((contact) => [contact.id, contact])),
    [contacts]
  );

  const strategy: AutoDistributeStrategy = keepTogether
    ? 'keepCompanyTogether'
    : balanceByCompany
      ? 'balanceByCompany'
      : 'even';

  const previewTeams = useMemo(() => {
    if (usePrevious && previousTeams.length > 0) {
      return applyPreviousTeams(previousTeams, poolIds);
    }

    return autoDistributeWithOptions(assignableContacts, {
      mode: distributeMode,
      teamCount: Math.min(
        Math.max(1, teamCount),
        Math.max(assignableContacts.length, 1)
      ),
      membersPerTeam: Math.min(
        Math.max(1, membersPerTeam),
        Math.max(assignableContacts.length, 1)
      ),
      strategy,
      existingTeams: teams,
    });
  }, [
    assignableContacts,
    distributeMode,
    membersPerTeam,
    poolIds,
    previousTeams,
    strategy,
    teamCount,
    teams,
    usePrevious,
  ]);

  const previewCounts = previewTeams.map((team) => team.memberIds.length);
  const maxCount = Math.max(assignableContacts.length, 1);
  const clampedTeamCount = Math.min(Math.max(1, teamCount), maxCount);
  const clampedMembersPerTeam = Math.min(Math.max(1, membersPerTeam), maxCount);
  const countOptions = Array.from(
    { length: maxCount },
    (_, index) => index + 1
  );
  const dropdownValue =
    distributeMode === 'membersPerTeam'
      ? clampedMembersPerTeam
      : clampedTeamCount;
  const canCreate = assignableContacts.length > 0 && previewTeams.length > 0;

  const handleCreateTeams = () => {
    if (!canCreate) {
      return;
    }
    onChange(previewTeams);
    setEditorMode('manual');
  };

  return (
    <div className={styles.editor}>
      <label className={styles.groupNameInline}>
        <span>Gruppenavn</span>
        <input
          className={styles.input}
          value={groupName}
          onChange={(event) => onGroupNameChange(event.target.value)}
          placeholder="T-lag"
        />
      </label>

      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={editorMode === 'auto'}
          className={editorMode === 'auto' ? styles.tabActive : styles.tab}
          onClick={() => setEditorMode('auto')}
        >
          <AutoAwesomeIcon />
          Del inn for meg (auto)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={editorMode === 'manual'}
          className={editorMode === 'manual' ? styles.tabActive : styles.tab}
          onClick={() => setEditorMode('manual')}
        >
          <GroupsIcon />
          Manuell inndeling
        </button>
      </div>

      {editorMode === 'manual' ? (
        <ManualTeamBoard
          contacts={contacts}
          teams={teams}
          currentUserId={currentUserId}
          onChange={onChange}
          onSelectMember={onSelectMember}
        />
      ) : (
        <div className={styles.autoLayout}>
          <div className={styles.column}>
            <section>
              <h4>1. Antall deltakere</h4>
              <div className={styles.participantCard}>
                <span className={styles.participantIcon} aria-hidden="true">
                  <GroupsIcon />
                </span>
                <div>
                  <strong>
                    {assignableContacts.length}{' '}
                    <span>
                      {participantWord(assignableContacts.length)} valgt
                    </span>
                  </strong>
                  <p>
                    {assignableContacts.length === contacts.length
                      ? 'Alle deltakere i sirkelen'
                      : 'Deltakere som kan fordeles (uten reserverte)'}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h4>2. Velg fordeling</h4>
              <label className={styles.radioRow}>
                <input
                  type="radio"
                  name="distribute-mode"
                  checked={distributeMode === 'teamCount'}
                  onChange={() => setDistributeMode('teamCount')}
                  disabled={usePrevious}
                />
                <span>
                  <strong>Antall lag</strong>
                  <span>Velg hvor mange lag som skal opprettes</span>
                </span>
              </label>
              <label className={styles.radioRow}>
                <input
                  type="radio"
                  name="distribute-mode"
                  checked={distributeMode === 'membersPerTeam'}
                  onChange={() => setDistributeMode('membersPerTeam')}
                  disabled={usePrevious}
                />
                <span>
                  <strong>Antall deltakere per lag</strong>
                  <span>Velg hvor mange deltakere hvert lag skal ha</span>
                </span>
              </label>
            </section>
          </div>

          <div className={styles.column}>
            <section>
              <h4>
                {distributeMode === 'membersPerTeam'
                  ? '3. Antall deltakere per lag'
                  : '3. Antall lag'}
              </h4>
              <select
                className={styles.select}
                value={dropdownValue}
                disabled={usePrevious}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  if (distributeMode === 'membersPerTeam') {
                    setMembersPerTeam(next);
                  } else {
                    setTeamCount(next);
                  }
                }}
              >
                {countOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>

              <div className={styles.previewCard}>
                <div className={styles.previewHeading}>
                  <span className={styles.previewBadge} aria-hidden="true">
                    !
                  </span>
                  <div>
                    <strong>Forhåndsvisning av fordeling</strong>
                    <p>
                      {assignableContacts.length}{' '}
                      {participantWord(assignableContacts.length)} fordeles på{' '}
                      {previewTeams.length} lag
                    </p>
                    <span className={styles.previewRange}>
                      {perTeamRangeLabel(previewCounts)}
                    </span>
                  </div>
                </div>
                <div className={styles.previewTeams}>
                  {previewTeams.map((team) => (
                    <div key={team.key} className={styles.previewTeam}>
                      <div className={styles.previewTeamRow}>
                        <span>{team.name}</span>
                        <span>
                          {team.memberIds.length}{' '}
                          {participantWord(team.memberIds.length)}
                        </span>
                      </div>
                      {showDetailedPreview && (
                        <ul className={styles.previewMembers}>
                          {team.memberIds.map((id) => {
                            const member = contactsById.get(id);
                            return (
                              <li key={id}>
                                {member?.name || 'Ukjent deltaker'}
                                {member?.company ? ` · ${member.company}` : ''}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <div className={styles.column}>
            <section>
              <h4>4. Ekstra innstillinger (valgfritt)</h4>
              <Toggle
                checked={keepTogether}
                disabled={usePrevious}
                onChange={(checked) => {
                  setKeepTogether(checked);
                  if (checked) {
                    setBalanceByCompany(false);
                  }
                }}
                label="Hold personer samlet"
                description="Forsøk å holde kolleger fra samme selskap på samme lag."
                hint="Deltakere med samme firma plasseres på samme lag så langt det går."
              />
              <Toggle
                checked={balanceByCompany}
                disabled={usePrevious}
                onChange={(checked) => {
                  setBalanceByCompany(checked);
                  if (checked) {
                    setKeepTogether(false);
                  }
                }}
                label="Balanser etter selskap"
                description="Fordel deltakere fra samme selskap jevnt på ulike lag."
                hint="Unngår at ett lag får de fleste fra samme firma."
              />
              <Toggle
                checked={usePrevious}
                disabled={previousTeams.length === 0}
                onChange={setUsePrevious}
                label="Bruk tidligere laginndeling"
                description="Hvis tilgjengelig, ta utgangspunkt i forrige inndeling."
                hint={
                  previousTeams.length === 0
                    ? 'Ingen tidligere laginndeling er lagret ennå.'
                    : 'Bruker forrige laginndeling som utgangspunkt.'
                }
              />
            </section>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setShowDetailedPreview((open) => !open)}
              >
                <VisibilityOutlinedIcon />
                {showDetailedPreview
                  ? 'Skjul detaljert visning'
                  : 'Forhåndsvis detaljert'}
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleCreateTeams}
                disabled={!canCreate}
              >
                Lag lagene
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
